import type { MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeftIcon } from './Icons';

/** The path part of a URL, without the query and the hash. */
const pathOf = (url: string) => url.split(/[?#]/)[0];

/**
 * The back link of a detail page (#595): a chevron and the name of the list, at the top left.
 *
 * When the person came from that list in this tab, the list link stored its
 * URL in the history state (`useListFrom`). Then the link goes back in the
 * history, so the list keeps its filters and its scroll position. Else it
 * opens `to`, a new copy of the list. A click with a modifier key opens `to`
 * in the usual way.
 */
export default function BackLink({ to, label }: { to: string; label: string }) {
  const navigate = useNavigate();
  const from = (useLocation().state as { from?: string } | null)?.from;
  const backToList = !!from && pathOf(from) === pathOf(to);
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!backToList || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(-1);
  };
  return (
    <Link to={backToList ? from! : to} onClick={onClick} data-testid="back-link"
      className="inline-flex items-center gap-1 py-2 -my-2 text-sm text-gray-500 hover:text-gray-900 transition-colors">
      <ChevronLeftIcon className="w-4 h-4" /> {label}
    </Link>
  );
}
